import { Outlet, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { List, X, ArrowRight } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const navigation = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "What I Do", href: "/what-i-do" },
  { name: "Thinking", href: "/thinking" },
  { name: "Contact", href: "/contact" },
];

export default function Layout() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white" data-testid="layout-container">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-[#E0E7ED]" data-testid="header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="font-['Cabinet_Grotesk',sans-serif] text-xl font-bold text-[#0D1B2A] tracking-tight"
              data-testid="logo-link"
            >
              Arora Sukhvinder
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`nav-link-premium text-sm font-medium tracking-wide transition-colors ${
                    location.pathname === item.href
                      ? "text-[#1565C0]"
                      : "text-[#3D5A73] hover:text-[#1565C0]"
                  }`}
                  data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-[#3D5A73] hover:text-[#1565C0] transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-b border-[#E0E7ED]"
              data-testid="mobile-nav"
            >
              <nav className="px-6 py-8 space-y-6">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block text-lg font-semibold tracking-wide ${
                      location.pathname === item.href
                        ? "text-[#1565C0]"
                        : "text-[#3D5A73]"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-20" />

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E0E7ED] bg-[#0D1B2A]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
            {/* Brand */}
            <div className="md:col-span-5">
              <Link 
                to="/" 
                className="font-['Cabinet_Grotesk',sans-serif] text-xl font-bold text-white tracking-tight"
                data-testid="footer-logo"
              >
                Arora Sukhvinder
              </Link>
              <p className="mt-6 text-[#90A4AE] leading-relaxed max-w-md">
                Business mentor focused on clarity, not complexity. Helping business owners understand their numbers and make better decisions.
              </p>
              <div className="mt-6 accent-bar"></div>
            </div>

            {/* Navigation */}
            <div className="md:col-span-3 md:col-start-8">
              <h4 className="text-xs font-bold tracking-[0.2em] uppercase text-[#00897B] mb-6">Navigation</h4>
              <nav className="space-y-4">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block text-[#90A4AE] hover:text-white transition-colors"
                    data-testid={`footer-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Contact CTA */}
            <div className="md:col-span-3">
              <h4 className="text-xs font-bold tracking-[0.2em] uppercase text-[#00897B] mb-6">Get in touch</h4>
              <Link
                to="/contact"
                className="group inline-flex items-center gap-3 text-white font-semibold hover:text-[#00897B] transition-colors"
                data-testid="footer-contact-cta"
              >
                Reach out <ArrowRight size={16} weight="bold" className="arrow-animate" />
              </Link>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-20 pt-8 border-t border-[#1E3A5F]">
            <p className="text-sm text-[#607D8B]">
              © {new Date().getFullYear()} Arora Sukhvinder. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
